<template>
    <tbody style="position: relative;">
        <Tooltip
            v-bind:hideIcon="this.hideIcon"
            v-bind:showCommentTooltip="this.showCommentTooltip"
            v-bind:comment="comment"
        />
        <tr v-on:click="open()" class="new-row-animation">
            <td v-if="!hideIcon">
                <ui-link class="tx-table-cell-icon"
                    v-bind:to="txLinkRouteParams"
                    v-bind:title="exitCode ? $t('tx.error_icon_tooltip', [exitCode]) : undefined"
                    v-bind:class="`tx-table-cell-icon--${iconKind}`">
                    <icon-tx-bounced v-once v-if="is_bounced"/>
                    <icon-tx-pending v-once v-else-if="pending" class="tx-pending-icon"/>
                    <icon-tx-error v-once v-else-if="!is_success"/>
                    <action-icon v-else
                        v-bind:actionName="action?.type"
                        v-bind:eventName="event"
                        v-bind:amount="amount"
                        v-bind:isOut="isOut"/>
                </ui-link>
            </td>
            <td
                v-bind:class="{ 'wide-timestamp': hideIcon }"
                class="tx-table-time"
                v-on:mouseover="showTimeTooltip = true"
                v-on:mouseleave="showTimeTooltip = false"
            >
                <div v-if="!pending" class="tx-table-time__value">
                    <ui-timeago class="tx-table-time__cell" v-bind:timestamp="timestamp"/>
                    <ui-tooltip
                        v-bind:value="formattedTimestamp "
                        position="right"
                        v-if="showTimeTooltip && this.$store.state.timeFormatData == 'relative'"
                    />
                </div>
                <div v-else class="tx-table-time__value">
                    {{ $t('address.tx_table.pending') }}
                </div>
                <!-- formattedTimestamp -->
                <!-- v-if="showTimeTooltip && this.$store.state.timeFormatData == 'absolute'" -->
            </td>
            <td>
                <div class="tx-table__cell tx-table__cell--align-right">
                    <span v-if="is_external || event === 'external'" class="muted" v-text="$t('address.tx_table.external')"/>
                    <span v-else-if="event === 'contract_deploy' || (event === 'change_wallet_key' && !sender)" class="muted" v-text="$t('common.empty')" />
                    <ui-address v-else
                        v-bind:address="sender"
                        v-bind:disabled="isSame(sender, address)"
                        v-bind:type="source_type"
                        v-bind:alias="source_alias"/>
                </div>
            </td>

            <!-- Message direction (in/out/log/swap): -->
            <td>
                <div class="tx-table__cell" style="padding: 0;">
                    <span v-if="is_service || isLog"
                        class="tx-table__badge tx-table__badge--service"
                        v-text="$t('address.tx_table.log')"/>

                    <span v-else-if="isOut"
                        class="tx-table__badge tx-table__badge--out"
                        v-text="$t('address.tx_table.output')"/>

                    <span v-else-if="is_swapped"
                        class="tx-table__badge tx-table__badge--swap"
                        v-text="$t('address.tx_table.swap')"/>

                    <span v-else
                        class="tx-table__badge tx-table__badge--in"
                        v-text="$t('address.tx_table.input')"/>
                </div>
            </td>

            <!-- Receivers: -->
            <td>
                <div class="tx-table__cell">
                    <ui-link v-if="is_aggregated" v-bind:to="txLinkRouteParams">
                        {{$tc('address.tx_table.address_count', outputCount)}}
                    </ui-link>

                    <template v-else-if="isLog">
                        {{$t('address.tx_table.nowhere')}}
                    </template>

                    <ui-address v-else
                        v-bind:address="receiver"
                        v-bind:disabled="isSame(receiver, address)"
                        v-bind:type="destination_type"
                        v-bind:alias="destination_alias"/>
                </div>
            </td>

            <!-- Transaction value (TON amount or action): -->
            <td class="column-wide">
                <div
                    class="tx-table__cell tx-table__cell--align-right amount-or-action"
                    style="position: relative;
                    padding-right: 26px;"
                >
                    <DesktopEventIcon
                        v-if="event !== 'nft_transfer' && event"
                        v-bind:event="event"
                    />

                    <action-badge
                        v-if="canShowActionBadge"
                        v-bind:action="action"
                        v-bind:amount="amount"
                        v-bind:op="op"
                        v-bind:meta="meta"
                        v-bind:from="from"
                        v-bind:to="to"
                        v-bind:is_out="isOut"
                        v-bind:event="event" />

                    <template v-else>
                        <div v-if="[
                            'jetton_burn',
                            'jetton_mint',
                            'auction_bid',
                        ].includes(event)">
                            <ui-inline-jetton
                                v-if="meta?.symbol !== 'GRAM'"
                                class="tx-table-jetton-link-standart"
                                v-bind:isOut="isOut"
                                v-bind:address="meta.jetton_address"
                                v-bind:value="action?.amount || amount"
                                v-bind:symbol="meta?.symbol || meta?.jetton_address"
                                v-bind:decimals="meta.decimals"
                                v-bind="meta"/>
                            <span v-else>
                                {{$ton(amount)}} GRAM
                            </span>
                        </div>

                        <template v-else-if="[
                            'sent_jetton',
                            'received_jetton',
                        ].includes(event)">
                            {{$ton(amount, meta?.decimals ?? 9)}}
                            <ui-link
                                style="margin-left: 5px;"
                                v-bind:to="{ name: 'token', params: {
                                    address: meta.jetton_address,
                                }}">
                                {{displayTicker(meta?.jetton?.symbol || meta?.symbol, meta?.jetton_address)}}
                            </ui-link>
                        </template>

                        <template v-else-if="event && event.endsWith('_dns') && meta?.domain">
                            <ui-link
                                style="margin-left: 5px;"
                                v-bind:to="{ name: 'address', params: {
                                    address: to,
                                }}">
                                {{truncateDomain(meta.domain)}}
                            </ui-link>
                        </template>

                        <template v-else-if="is_service || is_external || [
                            'external',
                        ].includes(event)">
                            <span class="muted" v-text="$t('common.empty')"/>
                        </template>

                        <!-- Events without TON amount -->
                        <template v-else-if="!amount"></template>

                        <template v-else>
                            <div>{{$ton(amount)}} GRAM</div>
                        </template>
                    </template>

                    <icon-encrypted-message v-once class="tx-table-operation-icon" v-if="encrypted || op === '0x2167da4b'"/>
                    <div class="tx-table-comment-icon">
                        <icon-message class="tx-table-operation-icon" v-if="comment"
                            v-on:mouseenter.native="showCommentTooltip = true"
                            v-on:mouseleave.native="showCommentTooltip = false"
                        />
                        <ui-tooltip
                            v-bind:value="comment"
                            position="left"
                            v-if="showCommentTooltip"
                        />
                    </div>
                </div>
            </td>

            <!-- Expand chevron: -->
            <td>
                <div class="tx-table__cell" v-if="!hideIcon">
                    <icon-expand class="tx-table-expand-caret" v-bind:class="{
                        'tx-table-expand-caret--expanded': isVisible,
                    }"/>
                </div>
            </td>
        </tr>

        <!-- Transaction extended details (accordeon block): -->
        <tr v-if="isVisible" class="tx-table-row-details">
            <td colspan="12">
                <div class="tx-table-inner-container">
                    <div class="tx-table-inner">
                        <div class="tx-table-inner__header" v-text="$t('tx.status')"/>
                        <template v-if="is_success">
                            {{$t('tx.exit_code_success', [exitCode])}}
                        </template>
                        <template v-else-if="is_bounced">
                            Bounced
                        </template>
                        <template v-else>
                            <template v-if="exitCode">
                                {{$t('tx.exit_code_error', [exitCode])}}
                            </template>
                            <template v-else>
                                {{$t('tx.status_failed')}}
                            </template>
                        </template>
                    </div>

                    <div class="tx-table-inner">
                        <div class="tx-table-inner__header" v-text="$t('tx.timestamp')"/>
                        <ui-datetime v-bind:timestamp="timestamp"/>
                    </div>

                    <div class="tx-table-inner">
                        <div class="tx-table-inner__header" v-text="$t('tx.hash')"/>

                        <ui-copy-button v-bind:copy="txHash" v-bind:successMessage="$t('tx.hash_copy_success')">
                            {{baseHex}}
                        </ui-copy-button>
                    </div>

                    <!-- <div class="tx-table-inner">
                        <div class="tx-table-inner__header" v-text="$t('tx.lt')"/>

                        <ui-copy-button v-bind:copy="txLt" v-bind:successMessage="$t('tx.lt_copy_success')">
                            {{txLt}}
                        </ui-copy-button>
                    </div> -->

                    <div class="tx-table-inner" v-if="fee">
                        <div class="tx-table-inner__header" v-text="$t('tx.fee')"/>
                        {{$ton(fee)}} GRAM
                    </div>

                    <div v-if="comment" class="tx-table-inner">
                        <div class="tx-table-inner__header" v-text="$t('tx.message')"/>
                        {{comment}}
                    </div>

                    <!-- Encrypted message -->
                    <div v-if="encrypted || op === '0x2167da4b'" class="tx-table-inner">
                        <div class="tx-table-inner__header" v-text="$t('tx.message')"/>
                        <span class="muted" v-text="$t('tx.encrypted')"/>
                    </div>
                </div>
            </td>
        </tr>
    </tbody>
</template>

<script>
import $bus from '~/eventBus.js';
import { mapState } from 'vuex';
import { isSameAddress } from '~/tonweb.js';
import { displayTicker } from '~/helpers.js';
import { base64ToHex } from '~/utils.js';
import IconTxError from '@img/icons/lucide/x.svg?vue';
import IconTxBounced from '@img/icons/lucide/undo-2.svg?vue';
import IconTxPending from '@img/icons/tonscan/round-loader.svg?vue';
import IconMessage from '@img/icons/tonscan/message-14.svg?vue';
import IconEncryptedMessage from '@img/icons/tonscan/encrypted-message.svg?vue';
import IconExpand from '@img/icons/lucide/chevron-down.svg?vue';

import UiInlineJetton from '~/components/address/TxRowInlineJetton.vue';
import ActionIcon from './TxRowActionIconMobile.vue';
import ActionBadge from './TxRowActionBadge.vue';
import DesktopEventIcon from './DesktopEventIcon.vue';
import Tooltip from '../other/Tooltip.vue';

export default {
    props: {
        address: String,
        date: String,
        from: String,
        is_service: Boolean,
        is_external: Boolean,
        is_aggregated: Boolean,
        is_success: Boolean,
        is_bounced: Boolean,
        is_swapped: Boolean,
        pending: Boolean,
        outputCount: Number,
        to: String,
        amount: String,
        message: String,
        timestamp: Number,
        fee: String,
        txHash: String,
        txLt: String,
        exitCode: Number,
        op: [Number, String],
        source_type: String,
        destination_type: String,
        source_alias: String,
        destination_alias: String,
        action: Object,
        event: String,
        meta: {
            type: Object,
            default: () => ({}),
        },
        hideIcon: Boolean,
        msg: Object,
        encrypted: Boolean,
    },

    data() {
        return {
            isVisible: false,
            showTimeTooltip: false,
            showCommentTooltip: false,
        };
    },

    computed: {
        /**
         * Tint of the round icon in the first column: red for anything that went
         * wrong, grey while pending, blue for swaps and service-like events, and
         * otherwise green in / red out like the amounts next to it.
         */
        iconKind() {
            if (this.is_bounced || !this.is_success) return 'error';
            if (this.pending) return 'gray';
            if (this.is_swapped || ['subscribe', 'unsubscribe', 'jetton_mint', 'deploy_nft', 'nft_purchase'].includes(this.event)) return 'blue';
            return this.isOut ? 'out' : 'in';
        },

        ...mapState({ dataSource: 'txTableSource' }),

        currentAccountContractType() {
            return this.isOut
                ? this.source_type
                : this.destination_type;
        },

        canShowActionBadge() {
            const isNftMessage = (this.action?.type || '').startsWith('nft:');

            // Avoid duplicates in events table
            if (this.event === 'jetton_burn') return false;

            // show nft badges only if they are opened on the wallet page:
            if (isNftMessage) {
                return this.currentAccountContractType === 'wallet';
            }

            return !!this.action;
        },

        sender() {
            return this.from;
        },

        receiver() {
            return this.to;
        },

        comment() {
            return this.message || this.action?.forward_payload;
        },

        isLog() {
            return !this.to;
        },

        isOut() {
            return this.isSame(this.from, this.address);
        },

        txLinkRouteParams() {
            return {
                name: 'tx',
                params: {
                    hash: base64ToHex(this.txHash),
                },
            };
        },

        baseHex() {
            return base64ToHex(this.txHash);
        },

        formattedTimestamp() {
            return this.formatTimestamp(this.timestamp);
        },
    },

    created() {
        $bus.$on('tx-close-all', () => {
            this.isVisible = false;
        });
    },

    beforeDestroy() {
        $bus.$off('tx-close-all');
    },

    methods: {
        displayTicker,

        open() {
            if (!this.isVisible) {
                $bus.$emit('tx-close-all');
            }

            if (!this.hideIcon) {
                this.isVisible = !this.isVisible;
            }
        },

        isSame(a, b) {
            return isSameAddress(a, b);
        },

        formatTimestamp(ts) {
            const date = new Date(ts);

            // Получаем компоненты даты
            const day = String(date.getDate()).padStart(2, '0'); // ДД
            const month = String(date.getMonth() + 1).padStart(2, '0'); // ММ (месяцы начинаются с 0)
            const year = String(date.getFullYear()).slice(-2); // ГГ (последние две цифры года)

            // Получаем компоненты времени
            const hours = String(date.getHours()).padStart(2, '0'); // ЧЧ
            const minutes = String(date.getMinutes()).padStart(2, '0'); // ММ
            const seconds = String(date.getSeconds()).padStart(2, '0'); // СС

            // Формируем строку в нужном формате
            return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
        },

        truncateDomain(domain) {
            if (domain && domain.length > 20) {
                return domain.slice(0, 8) + '...' + domain.slice(-8);
            }
            return domain;
        },
    },

    components: {
        ActionBadge,
        ActionIcon,
        IconTxPending,
        IconTxError,
        IconMessage,
        IconEncryptedMessage,
        IconExpand,
        IconTxBounced,
        UiInlineJetton,
        DesktopEventIcon,
        Tooltip,
    },
};
</script>

<style lang="scss">
.tx-pending-icon {
    animation: 2s pendingRotation infinite linear;

    &__desktop {
        width: 14px;
        height: 14px;
        margin-bottom: -2px;
    }
}

.tx-confirmed-icon {
    &__desktop {
        width: 14px;
        height: 14px;
        transform: scale(1.3);
        margin-bottom: -2px;
    }
}

@keyframes pendingRotation {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
.amount-or-action {
    display: inline-flex;
    gap: 12px;
}
.comment-tooltip {
    position: absolute;
    object-position: bottom;
    right: 40px;
    height: auto;
    top: 6px;
    border: 1px solid var(--card-border-color);
    border-radius: 12px;
    box-shadow: 0 .5rem 1.2rem var(--card-box-shadow-color);
    padding: 12px 14px;
    background-color: var(--body-background);
    z-index: 99999;
    transition: .3s;
    max-width: 300px;

    // Make triangle tail
    &::after {
        content: '';
        display: block;
        position: absolute;
        top: 10px;
        right: -8px;
        width: 8px;
        height: 8px;
        background: var(--body-background);
        border-right: 1px solid var(--card-border-color);
        border-bottom: 1px solid var(--card-border-color);
        transform: rotateZ(-45deg) translateX(-4.3px);
    }
}

// Make timestamp for events table, so addreses would be in the center
.wide-timestamp {
    min-width: 220px;
}

.comment-tooltip-animation-enter-to, .comment-tooltip-animation-leave {
    opacity: 1;
}
.comment-tooltip-animation-enter, .comment-tooltip-animation-leave-to {
    opacity: 0;
}
.tx-table-time {
    position: relative;
    cursor: default;

    &__cell {
        display: flex;
        align-items: center;
        max-width: 100%;
        box-sizing: border-box;
        width: 100%;
    }

    &__value {
        display: inline-block;
        position: relative;
        margin: 0 18px;
    }
}

@media screen and (max-width: 1149px) {
    .wide-timestamp {
        min-width: auto;
    }
}
</style>
